import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form } from 'antd'
import _ from 'lodash'
import { CometChatButton } from 'components/base/Button'
import { CometChatDrawer } from 'components/base/Drawer'
import { CometChatTextArea } from 'components/base/TextArea'
import { CometChatInput } from 'components/base/Input'
import { RootState } from 'redux/types'
import useDrawerPanel from '../../hooks/useDrawerPanel'
import style from './style.module.scss'

interface AiInstructionFormProps {
  mode: 'add' | 'edit'
  /** Id of the instruction being edited. */
  id?: string
  onClose: () => void
  previewMode?: boolean
  onPreviewInteraction?: () => void
}

interface AiInstructionFormValues {
  name: string
  prompt: string
}

const AiInstructionForm: React.FC<AiInstructionFormProps> = ({
  mode,
  id,
  onClose,
  previewMode = false,
  onPreviewInteraction,
}) => {
  const [form] = Form.useForm<AiInstructionFormValues>()
  const dispatch = useDispatch()
  const aiInstructionsActive = useSelector(
    (state: RootState) => state.customBots.aiInstructionsActive,
  )
  const areAiInstructionsLoading = useSelector(
    (state: RootState) => state.customBots.areAiInstructionsLoading,
  )
  const drawer = useDrawerPanel(onClose)

  const isEdit = mode === 'edit'

  useEffect(() => {
    if (isEdit && id) {
      dispatch({ type: 'customBots/GET_AI_INSTRUCTION', payload: { id } })
    }
  }, [dispatch, isEdit, id])

  // Matched on id, not just presence: `aiInstructionsActive` still holds the last
  // instruction opened, and filling from it would save its values onto this one.
  useEffect(() => {
    if (!isEdit || aiInstructionsActive?.id !== id) return
    form.setFieldsValue({
      name: aiInstructionsActive.name,
      prompt: aiInstructionsActive.prompt,
    })
  }, [aiInstructionsActive, form, isEdit, id])

  const onFinish = (values: AiInstructionFormValues) => {
    if (previewMode) {
      onPreviewInteraction?.()
      return
    }
    const payload = { name: _.trim(values.name), prompt: _.trim(values.prompt) }
    if (isEdit && id) {
      dispatch({
        type: 'customBots/UPDATE_AI_INSTRUCTION',
        id,
        payload,
        onComplete: drawer.closeOnSaved,
      })
    } else {
      dispatch({ type: 'customBots/ADD_AI_INSTRUCTION', payload, onComplete: drawer.closeOnSaved })
    }
  }

  return (
    <CometChatDrawer
      title={isEdit ? 'Edit Instruction' : 'Add Instruction'}
      placement="right"
      closable
      open={drawer.open}
      afterOpenChange={drawer.onAfterOpenChange}
      width={400}
      className="moduleFormDrawer"
      onClose={drawer.close}
      maskClosable={false}
    >
      <div className={style['cc-ai-instruction-form']}>
        <Form
          form={form}
          hideRequiredMark
          onFinish={onFinish}
          initialValues={{ name: '', prompt: '' }}
        >
          <div className={style['cc-ai-instruction-form__fields']}>
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please enter the instruction name' }]}
            >
              <CometChatInput
                placeholder="Enter instruction name"
                label="Instruction Name"
                disabled={previewMode}
              />
            </Form.Item>
            <Form.Item
              name="prompt"
              rules={[{ required: true, message: 'Please enter the prompt' }]}
            >
              <CometChatTextArea
                label="Prompt"
                helpText="Describes how the bot should behave in a conversation"
                rows={6}
                disabled={previewMode}
                placeholder="Enter prompt"
              />
            </Form.Item>
          </div>

          <div className={style['cc-ai-instruction-form__footer']}>
            <CometChatButton
              hierarchy="secondary"
              size="lg"
              htmlType="button"
              onClick={drawer.close}
            >
              Cancel
            </CometChatButton>
            <CometChatButton
              hierarchy="black"
              size="lg"
              htmlType="submit"
              loading={areAiInstructionsLoading}
            >
              {isEdit ? 'Save' : 'Add'}
            </CometChatButton>
          </div>
        </Form>
      </div>
    </CometChatDrawer>
  )
}

export default AiInstructionForm
